/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import camelCase from 'lodash/camelCase';
import {optionsHelper} from '../helpers/options.helper';

let internalId = Date.now();

/**
 * @typedef {object} Directive
 * @property {object} options Directive options container
 * @property {function} init  The initalize method
 * @property {function} destroy The destroy method
 */

/**
 * Simple directive controlled via mutation observer.
 */
export const compilerService = {
  /**
   * @readonly
   *
   * @type {Map<string, Directive>}
   */
  directives: new Map(),

  /**
   * @readonly
   *
   * @type {Set<Directive>}
   */
  selectors: new Set(),

  /**
   * @type {MutationObserver}
   */
  observer: null,

  /**
   * Register new directive which will managed by mutation observer.
   *
   * @param {string}    name      Unique directive name
   * @param {Directive} directive The assoicated directive with `init()` and `destroy()` methods.
   */
  register(name, directive) {
    const methods = ['init', 'destroy'];

    if (!methods.every((method) => typeof directive[method] === 'function')) {
      throw new TypeError(`The "${name}" directive does not have required methods: [${methods.join(', ')}].`);
    }

    if (name) {
      this.directives.set(name, directive);
    }

    if (directive.selectors) {
      this.selectors.add(directive);
    }
  },

  /**
   * Register app root mutaion observer
   */
  observe(appRoot = document.body) {
    if (this.observer) {
      this.observer.disconnect();
    }

    this.observer = new MutationObserver((mutations) => mutations.map((mutation) => this.onMutation(mutation)));
    this.observer.observe(appRoot, {
      childList: true,
      subtree: true,
    });

    this.compile(appRoot);
  },

  /**
   * Call `directive.init()` method when element get added to DOM tree
   *
   * @param {Element} element The added element
   */
  compile(element) {
    if (element.ambid) {
      return null;
    } else {
      element.ambid = (internalId--).toString(16);
    }

    Array.from(element.attributes).forEach((attr) => {
      this.compileElementByAttribute(element, attr);
    });

    this.selectors.forEach((directive) => {
      const match = directive.selectors.some((selector) => element.matches(selector));

      if (match) {
        const {options: userOptions, presets = '', ...inlineOptions} = element.dataset;
        const options = optionsHelper.getOptions(
          directive.options || {},
          ['default', presets, userOptions, inlineOptions],
          {element}
        );

        directive.init(element, options);
      }
    });

    Array.from(element.children).forEach((child) => {
      this.compile(child);
    });
  },

  /**
   * Try to call `directive.destroy()` method when element get removed from DOM tree
   *
   * @param {Element} element The removed element
   */
  destroy(element) {
    if (!element.ambid || element.parentElement) {
      return null;
    }

    Array.from(element.children).forEach((child) => {
      this.destroy(child);
    });

    Array.from(element.attributes).forEach((attr) => {
      const name = camelCase(attr.name.replace(/data-/i, ''));

      if (this.directives.has(name)) {
        this.directives.get(name).destroy(element);
      }
    });

    this.selectors.forEach((directive) => {
      const match = directive.selectors.some((selector) => element.matches(selector));

      if (match) {
        directive.destroy(element);
      }
    });

    delete element.ambid;
  },

  /**
   * Compile single directive for the given element by attribute
   *
   * @param {HTMLElement} element HTML element to compile
   * @param {Attr}        attr    Element attribute to compile
   */
  compileElementByAttribute(element, attr) {
    const name = camelCase(attr.name.replace(/^data-/i, ''));

    if (this.directives.has(name)) {
      const directive = this.directives.get(name);
      const {options: userOptions, presets = '', ...inlineOptions} = element.dataset;
      const options = optionsHelper.getOptions(
        directive.options || {},
        ['default', presets, element.dataset[name], attr.value, userOptions, inlineOptions],
        {element, attr}
      );

      directive.init(element, options);
    }
  },

  /**
   * Fires when DOM elements add or remove
   *
   * @protected
   *
   * @param {MutationRecord} record
   */
  onMutation(record) {
    if (record.type === 'childList') {
      record.addedNodes.forEach((element) => !(element instanceof Element) || this.compile(element));
      record.removedNodes.forEach((element) => !(element instanceof Element) || this.destroy(element));
    }
  },
};
